// utils/exportComparisonToCSV.js

/**
 * Escape a single cell value for RFC 4180-compliant CSV:
 *   - Wraps every value in double-quotes
 *   - Escapes embedded double-quotes by doubling them
 *   - Removes raw newlines inside cells (replaces with space) so rows stay intact
 */
const csvCell = (value) => {
  const str = String(value ?? "")
    .replace(/\r?\n/g, " ")   // flatten multi-line text to one line per cell
    .replace(/"/g, '""');     // escape embedded double-quotes
  return `"${str}"`;
};

/** Convert a 2-D array of values into a CSV string. */
const toCsvString = (rows) =>
  rows.map((row) => row.map(csvCell).join(",")).join("\r\n");

/**
 * Trigger a client-side CSV file download.
 *
 * Prepends a UTF-8 BOM (0xFEFF) so Excel opens the file with the correct
 * encoding without any import wizard.
 */
const downloadCsv = (csvString, filename) => {
  const blob = new Blob(["\uFEFF" + csvString], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

/**
 * Build a sanitised filename from device names.
 * e.g. "comparison_Model-6491_vs_Model-6492.csv"
 */
const buildFilename = (name1, name2) => {
  const sanitise = (s) =>
    (s || "Device")
      .replace(/[^a-zA-Z0-9_\-. ]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 40);
  return `comparison_${sanitise(name1)}_vs_${sanitise(name2)}.csv`;
};

/**
 * Convert a DeviceComparator JSON result into a CSV file and download it.
 *
 * Expected shape of `comparisonData`:
 * {
 *   title:            string,
 *   device1:          { name, role, file },
 *   device2:          { name, role, file },
 *   model_used:       string,
 *   summary:          { Same, Similar, Different },
 *   comparison_table: [{ characteristic, device1_value, device2_value, verdict, justification }],
 *   conclusion:       string,
 * }
 *
 * @param {object} comparisonData  - The `content` payload from the WebSocket response.
 * @param {string} [filename]      - Override the auto-generated filename.
 */
export const exportComparisonToCSV = (comparisonData, filename) => {
  const {
    title = "Substantial Equivalence Comparison Table",
    device1 = {},
    device2 = {},
    model_used = "",
    summary = {},
    comparison_table = [],
    conclusion = "",
  } = comparisonData;

  const d1Name = device1.name || "Device 1";
  const d2Name = device2.name || "Device 2";
  const outputFilename = filename || buildFilename(d1Name, d2Name);

  const rows = [];

  // ── Section 1: Report header ──────────────────────────────────────────────
  rows.push([title]);
  rows.push([`Generated by model: ${model_used}`]);
  rows.push([]);

  // ── Section 2: Device identifiers ────────────────────────────────────────
  rows.push(["", "Device 1 (Subject)", "Device 2 (Predicate)"]);
  rows.push(["Name",  d1Name,  d2Name]);
  rows.push(["Role",  device1.role || "Subject Device",
                      device2.role || "Predicate / Comparator Device"]);
  rows.push(["File",  device1.file || "", device2.file || ""]);
  rows.push([]);

  // ── Section 3: Verdict summary ────────────────────────────────────────────
  rows.push(["Verdict Summary", "Count"]);
  rows.push(["Same",      summary.Same      ?? 0]);
  rows.push(["Similar",   summary.Similar   ?? 0]);
  rows.push(["Different", summary.Different ?? 0]);
  rows.push([
    "Total",
    (summary.Same ?? 0) + (summary.Similar ?? 0) + (summary.Different ?? 0),
  ]);
  rows.push([]);

  // ── Section 4: Comparison table ───────────────────────────────────────────
  rows.push([
    "Characteristic",
    `Device 1 — ${d1Name}`,
    `Device 2 — ${d2Name}`,
    "Verdict",
    "Justification",
  ]);

  comparison_table.forEach((row) => {
    rows.push([
      row.characteristic  ?? "",
      row.device1_value   ?? "",
      row.device2_value   ?? "",
      row.verdict         ?? "",
      row.justification   ?? "",
    ]);
  });

  rows.push([]);

  // ── Section 5: Regulatory conclusion ─────────────────────────────────────
  rows.push(["Regulatory Conclusion"]);
  rows.push([conclusion]);

  downloadCsv(toCsvString(rows), outputFilename);
};
